import { FC, useEffect, useState } from "react";
import { Modal, Button, Row, Col } from "react-bootstrap";
import { NetworkAccountService } from "../../../shared/services/contentNetwork/networkAccounts/networkAccountService";
import { fetchReportingNetworkConfigs } from "@services/referenceData/reportingNetworkConfigService";

import NetworkResourceForm from "./NetworkResourceForm";
import SubresourcePickerStep from "./SubresourcePickerStep";
import showAlert from "../../../shared/utils/generic";

import "./styles.scss";
import { NetworkResource } from "@models/networkResource";
import socialMediaData from "src/shared/data/socialMediaData";

interface Props {
  show: boolean;
  onClose: () => void;
  organizationId: number;
  onResourceCreated: () => void;
}

type NetworkType = "meta" | "pinterest" | "snapchat" | "tiktok" | "google" | "linkedin" | "vibe";
type ResourceType = "account" | "page" | "profile" | "board" | "youtube_channel";

const NETWORK_TYPES: Record<NetworkType, ResourceType[]> = {
  meta: ["account", "page"],
  pinterest: ["account", "board"],
  snapchat: ["account", "profile"],
  tiktok: ["account"],
  google: ["account", "youtube_channel"],
  // LinkedIn sponsors content as an organisation page, the way Meta sponsors as a page.
  linkedin: ["account", "page"],
  // Vibe is account-only, and it is the only network that is. Campaigns, creatives and
  // audiences all hang off the advertiser, which IS the account resource — there is no page,
  // board or profile to choose.
  vibe: ["account"],
};

const TYPE_CONFIG: Record<ResourceType, { label: string; icon: string; description: string }> = {
  account: {
    label: "Account",
    icon: "bi-person-circle",
    description: "Main advertising account",
  },
  page: {
    label: "Page",
    icon: "bi-file-earmark",
    description: "Facebook, Instagram or LinkedIn page",
  },
  profile: {
    label: "Profile",
    icon: "bi-person-badge",
    description: "Snapchat profile",
  },
  board: {
    label: "Board",
    icon: "bi-grid-3x3",
    description: "Pinterest board",
  },
  youtube_channel: {
    label: "YouTube Channel",
    icon: "bi-youtube",
    description: "YouTube channel for video uploads",
  },
};

const NetworkResourceCreateModal: FC<Props> = ({
  show,
  onClose,
  organizationId,
  onResourceCreated,
}) => {
  const [step, setStep] = useState(1);
  const [selectedNetwork, setSelectedNetwork] = useState<NetworkType | null>(null);
  const [selectedType, setSelectedType] = useState<ResourceType | null>(null);
  const [resource, setResource] = useState<Partial<NetworkResource>>({
    is_active: true,
  });
  const [loading, setLoading] = useState(false);

  // Which networks are live, straight from the reporting network configs — the same switch that
  // decides whether a network appears as a publisher on a campaign. Without this the two
  // disagreed: a network could be turned off everywhere else and still be offered here, so
  // someone could set up an account for a network they cannot actually publish to.
  const [liveNetworks, setLiveNetworks] = useState<string[] | null>(null);

  useEffect(() => {
    if (!show) return;
    fetchReportingNetworkConfigs()
      // Only publisher networks. This modal connects ad accounts, and a direct delivery
      // channel has none to connect; it appears in the config list purely so it can be
      // drawn on a chart.
      .then((configs) =>
        setLiveNetworks(
          configs.map((c) => c.publisher_network).filter((n): n is string => Boolean(n)),
        ),
      )
      // The endpoint only ever returns active rows, so a failure here means we do not know.
      // Show everything rather than block setup on a reference-data hiccup.
      .catch(() => setLiveNetworks(Object.keys(socialMediaData)));
  }, [show]);

  const accountService = new NetworkAccountService();

  const handleClose = () => {
    setStep(1);
    setSelectedNetwork(null);
    setSelectedType(null);
    setResource({ is_active: true });
    onClose();
  };

  const handleNetworkSelect = (network: NetworkType) => {
    setSelectedNetwork(network);
    setStep(2);
  };

  const handleTypeSelect = (type: ResourceType) => {
    setSelectedType(type);
    setStep(3);
  };

  const handleBack = () => {
    if (step === 2) {
      setStep(1);
      setSelectedNetwork(null);
    } else if (step === 3) {
      setStep(2);
      setSelectedType(null);
    }
  };

  const handleSubmit = async () => {
    if (!selectedNetwork || !selectedType) return;

    // Validate required fields
    if (!resource.name || !resource.external_id) {
      showAlert("Please fill in all required fields", "error", 3000);
      return;
    }

    setLoading(true);
    try {
      const newResource = new NetworkResource();
      newResource.organization_id = organizationId;
      newResource.network = selectedNetwork;
      newResource.type = selectedType;
      newResource.name = resource.name;
      newResource.external_id = resource.external_id;
      newResource.is_active = resource.is_active ?? true;
      if (resource.auth_keys && Object.keys(resource.auth_keys).length > 0) {
        newResource.auth_keys = resource.auth_keys;
      }

      await accountService.createNetworkResource(newResource);

      showAlert("Resource created successfully", "success", 3000);
      onResourceCreated();
      handleClose();
    } catch (err) {
      console.error("Failed to create resource:", err);
      showAlert("Failed to create resource", "error", 3000);
    } finally {
      setLoading(false);
    }
  };

  const renderNetworkSelection = () => (
    <div>
      <h5 className="mb-3">Select Network</h5>
      <Row className="g-3">
        {Object.entries(socialMediaData)
          .filter(([key]) => liveNetworks === null || liveNetworks.includes(key))
          .map(([key, network]) => (
            <Col xs={6} md={4} key={key}>
              <div
                className="network-card"
                onClick={() => handleNetworkSelect(key as NetworkType)}
              >
                <div
                  className="network-icon"
                  style={{
                    backgroundColor: network.color,
                    color: key === "snapchat" ? "#000" : "#fff",
                  }}
                >
                  <i className={network.icon}></i>
                </div>
                <div className="network-label">{network.label}</div>
              </div>
            </Col>
          ))}
      </Row>
    </div>
  );

  const renderTypeSelection = () => {
    if (!selectedNetwork) return null;

    const availableTypes = NETWORK_TYPES[selectedNetwork];

    return (
      <div>
        <div className="d-flex align-items-center gap-2 mb-3">
          <div
            className="rounded-circle d-flex align-items-center justify-content-center"
            style={{
              width: "32px",
              height: "32px",
              backgroundColor: socialMediaData[selectedNetwork].color,
              color: selectedNetwork === "snapchat" ? "#000" : "#fff",
            }}
          >
            <i className={socialMediaData[selectedNetwork].icon}></i>
          </div>
          <h5 className="mb-0">Select Resource Type</h5>
        </div>

        <Row className="g-3">
          {availableTypes.map((type) => {
            const config = TYPE_CONFIG[type];
            return (
              <Col xs={6} key={type}>
                <div className="type-card" onClick={() => handleTypeSelect(type)}>
                  <div className="type-icon">
                    <i className={`bi ${config.icon}`}></i>
                  </div>
                  <div className="type-label">{config.label}</div>
                  <div className="type-description">{config.description}</div>
                </div>
              </Col>
            );
          })}
        </Row>
      </div>
    );
  };

  const renderForm = () => {
    if (!selectedNetwork || !selectedType) return null;

    // Non-account types use the subresource picker
    if (selectedType !== "account") {
      return (
        <SubresourcePickerStep
          organizationId={organizationId}
          network={selectedNetwork}
          resourceType={selectedType}
          onComplete={() => {
            onResourceCreated();
            handleClose();
          }}
          onBack={handleBack}
          onCancel={handleClose}
        />
      );
    }

    const networkData = socialMediaData[selectedNetwork];
    const typeConfig = TYPE_CONFIG[selectedType];

    return (
      <div>
        <div className="d-flex align-items-center gap-2 mb-3 pb-3 border-bottom">
          <div
            className="rounded-circle d-flex align-items-center justify-content-center"
            style={{
              width: "32px",
              height: "32px",
              backgroundColor: networkData.color,
              color: selectedNetwork === "snapchat" ? "#000" : "#fff",
            }}
          >
            <i className={networkData.icon}></i>
          </div>
          <div>
            <h5 className="mb-0">
              {networkData.label} {typeConfig.label}
            </h5>
            <small className="text-muted">{typeConfig.description}</small>
          </div>
        </div>

        <NetworkResourceForm
          resource={resource}
          onChange={setResource}
          showAuthKeys={true}
          resourceNetwork={selectedNetwork}
        />
      </div>
    );
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>Add Network Resource</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {step === 1 && renderNetworkSelection()}
        {step === 2 && renderTypeSelection()}
        {step === 3 && renderForm()}
      </Modal.Body>

      {!(step === 3 && selectedType !== "account") && (
        <Modal.Footer>
          <div className="d-flex justify-content-between w-100">
            <div>
              {step > 1 && (
                <Button variant="outline-secondary" onClick={handleBack}>
                  <i className="bi bi-arrow-left me-2"></i>
                  Back
                </Button>
              )}
            </div>
            <div className="d-flex gap-2">
              <Button variant="secondary" onClick={handleClose}>
                Cancel
              </Button>
              {step === 3 && selectedType === "account" && (
                <Button variant="primary" onClick={handleSubmit} disabled={loading}>
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      Creating...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-check-circle me-2"></i>
                      Create Resource
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>
        </Modal.Footer>
      )}
    </Modal>
  );
};

export default NetworkResourceCreateModal;